import {
	getTodayISO,
	isSameDay,
	safeDateParse,
} from '../../../shared/utils/dateUtils';

export function normalizeToLocalDateISO(
	dateValue: string | Date | null | undefined,
): string | null {
	const parsed = safeDateParse(dateValue);
	if (!parsed) return null;

	const year = parsed.getFullYear();
	const month = String(parsed.getMonth() + 1).padStart(2, '0');
	const day = String(parsed.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
}

export function isDateToday(dateValue: string | Date | null | undefined): boolean {
	const normalized = normalizeToLocalDateISO(dateValue);
	return normalized ? normalized === getTodayISO() : false;
}

export function getTodoDueDateValue(todo: Record<string, unknown>): string | null | undefined {
	const candidates = [
		todo.due_date,
		todo.dueDate,
		todo.scheduledDate,
		todo.scheduled_date,
	];
	for (const candidate of candidates) {
		if (candidate != null && String(candidate).trim() !== '') {
			return String(candidate);
		}
	}
	return undefined;
}

export function filterTodosForToday<T extends Record<string, unknown>>(todos: T[]): T[] {
	return todos.filter((todo) => isDateToday(getTodoDueDateValue(todo)));
}

function isOrganizationBroadcast(message: Record<string, unknown>): boolean {
	return Boolean(message.entireOrgCreatedDate || message.entireOrgCreatedTime);
}

function getDisplayCreatedFields(message: Record<string, unknown>): {
	createdDate?: string;
	createdTime?: string;
} {
	const useEntireOrgFields = isOrganizationBroadcast(message);
	return {
		createdDate: useEntireOrgFields
			? ((message.entireOrgCreatedDate ?? message.createdDate) as string | undefined)
			: (message.createdDate as string | undefined),
		createdTime: useEntireOrgFields
			? ((message.entireOrgCreatedTime ?? message.createdTime) as string | undefined)
			: (message.createdTime as string | undefined),
	};
}

export function getBroadcastMessageDate(message: Record<string, unknown>): Date | null {
	const isoField = (message.createdAt ?? message.created_at) as string | undefined;
	if (isoField) {
		const isoDate = safeDateParse(isoField);
		if (isoDate) return isoDate;
	}

	const { createdDate, createdTime } = getDisplayCreatedFields(message);
	if (!createdDate) return null;

	const baseDate = safeDateParse(createdDate);
	if (!baseDate) return null;

	if (!createdTime) return baseDate;

	const timeParts = String(createdTime).trim().split(/\s+/);
	const clock = timeParts[0] || '';
	const ampm = (timeParts[1] || '').toUpperCase();
	const [hStr, mStr, sStr] = clock.split(':');
	let hours = Number(hStr) || 0;
	const minutes = Number(mStr) || 0;
	const seconds = Number(sStr) || 0;

	if (ampm === 'PM' && hours < 12) hours += 12;
	if (ampm === 'AM' && hours === 12) hours = 0;

	baseDate.setHours(hours, minutes, seconds, 0);
	return baseDate;
}

export function filterBroadcastsForToday<T extends Record<string, unknown>>(messages: T[]): T[] {
	const today = getTodayISO();
	return messages.filter((message) => {
		const messageDate = getBroadcastMessageDate(message);
		return messageDate ? isSameDay(messageDate, today) : false;
	});
}
